// src/hooks/useFetchPotentialAssignees.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { Sphere } from 'types/global';
import { ApiClient } from '@center-technologies/shared-api-clients';
import type { FilterExpression } from 'types/filters';

export interface OptionType {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: import('types/glyph').GlyphColor;
  imageId?: string;
  /** Center sphere info (from center_center perspective assignment) */
  center?: {
    id: string;
    name: string;
    glyph?: string;
    glyphColor?: import('types/glyph').GlyphColor;
    imageId?: string;
  };
}

export interface UseFetchPotentialAssigneesParams {
  organizationId: string;
  schemaSystemName: string;
  perspectiveSystemName: string;
  nearbySphereId: string | null;
  nearbyMode: boolean
  selectedOption: OptionType | null;
  apiClient: ApiClient; // <-- ApiClient instance for making requests
  limit?: number;
  contextFilters?: FilterExpression[] | null;
  /** When false, eagerly fetch on mount. Default true — data loads only when fetchData() is called (e.g., on dropdown open). */
  lazy?: boolean;
}

export interface UseFetchPotentialAssigneesReturn {
  options: OptionType[];
  potentialAssignees: Sphere[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchData: (mode?: 'append' | 'replace') => Promise<void>;
  resetData: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  triggerInitialFetch: () => void;
}

export const useFetchPotentialAssignees = (
  params: UseFetchPotentialAssigneesParams
): UseFetchPotentialAssigneesReturn => {
  const {
    organizationId,
    schemaSystemName,
    perspectiveSystemName,
    nearbySphereId,
    nearbyMode,
    selectedOption,
    apiClient,
    limit = 20,
    contextFilters,
    lazy = true,
  } = params;

  const [options, setOptions] = useState<OptionType[]>([]);
  const [potentialAssignees, setPotentialAssignees] = useState<Sphere[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false);

  // Stable serialization of contextFilters for dependency comparison
  const contextFiltersKey = useRef<string>('');
  const newContextFiltersKey = contextFilters ? JSON.stringify(contextFilters) : '';

  // Initial fetch on mount (skipped when lazy=true)
  useEffect(() => {
    if (!lazy && !initialFetchDone && apiClient) {
      contextFiltersKey.current = newContextFiltersKey;
      setInitialFetchDone(true);
      if (!lazy) {
        fetchData('replace');
      }
    }
  }, [apiClient, initialFetchDone, lazy]);

  // Reset data whenever search term changes
  useEffect(() => {
    if (!initialFetchDone) return;
    setPage(1);
    setHasMore(true);
    fetchData('replace');
  }, [searchTerm]);

  // Re-fetch when contextFilters change
  useEffect(() => {
    if (!initialFetchDone) return;
    if (newContextFiltersKey === contextFiltersKey.current) return;
    contextFiltersKey.current = newContextFiltersKey;
    setPage(1);
    setHasMore(true);
    fetchData('replace');
  }, [newContextFiltersKey, initialFetchDone]);

  // Initialize with selectedOption if no options exist
  useEffect(() => {
    if (options.length === 0 && selectedOption) {
      setOptions([selectedOption]);
    }
  }, [selectedOption]); // Only depend on selectedOption, not options


  // returns an array of options based on an array of potential assignees
  const getOptionsFromAssignees = (assignees: Sphere[]): OptionType[] => {
    return assignees.map((assignee) => {
      // Extract center from center_center perspective assignment
      let center: OptionType['center'] = undefined;
      if (assignee.assignedPerspectives) {
        const centerPerspective = assignee.assignedPerspectives.find(
          (p) => p.perspectiveProperties.system_name === 'center_center'
        );
        const centerSphere = centerPerspective?.assigned?.[0]?.sphere;
        if (centerSphere) {
          const centerImageId = Array.isArray(centerSphere.properties?.['imageId'])
            ? centerSphere.properties['imageId'][0]
            : centerSphere.properties?.['imageId'];
          center = {
            id: centerSphere.id,
            name: centerSphere.properties.name,
            glyph: centerSphere.glyph,
            glyphColor: centerSphere.glyphColor,
            imageId: centerImageId,
          };
        }
      }

      return {
        name: assignee.properties.name,
        code: assignee.id,
        glyph: assignee.glyph,
        glyphColor: assignee.glyphColor,
        imageId: assignee.properties['imageId'],
        center,
      };
    });
  };

  const fetchData = useCallback(
    async (mode = 'append') => {
      if (isLoading || (!hasMore && mode === 'append')) {
        return;
      }

      setIsLoading(true);
      try {
        
        // Build the request payload
        const requestBody: {
          schemaSystemName: string;
          perspectiveSystemName: string;
          limit: number;
          page: number;
          searchTerm: string;
          nearbySphereId?: string;
          contextFilters?: FilterExpression[];
        } = {
          schemaSystemName,
          perspectiveSystemName,
          limit,
          page: mode === 'replace' ? 1 : page,
          searchTerm,
        };

        // Only include nearbySphereId if it's not null/undefined and nearbyMode is true
        if (nearbyMode && nearbySphereId) {
          requestBody.nearbySphereId = nearbySphereId;
        }

        // Include context filters when active context is selected
        if (contextFilters && contextFilters.length > 0) {
          requestBody.contextFilters = contextFilters;
        }

        // Use ApiClient for the request (handles auth, retries, errors automatically)
        const data = await apiClient.post<{ spheres: Sphere[]; pagination: { hasNextPage: boolean } }>(
          '/search/potential-assignees',
          requestBody
        );

        const fetchedAssignees = data.spheres || [];
        const hasNextPageFromAPI = data.pagination?.hasNextPage ?? false;

        // Update state with fetched data
        if (mode === 'replace') {
          setPotentialAssignees(fetchedAssignees);
        } else {
          setPotentialAssignees((prev) => [...prev, ...fetchedAssignees]);
        }
        setHasMore(hasNextPageFromAPI);

        const newOptions = getOptionsFromAssignees(fetchedAssignees);

        if (mode === 'replace') {
          if (newOptions.length === 0) {
            setError('No potential assignees found.');
            setOptions([{glyph:'info-circle', name: 'No matches for ' + searchTerm, code: '' }]);
          } else {
            // Preserve selectedOption if it exists and isn't in the new options
            let finalOptions = newOptions;
            if (selectedOption?.code && !newOptions.some(opt => opt.code === selectedOption.code)) {
              finalOptions = [selectedOption, ...newOptions];
            }
            setOptions(finalOptions);
          }
        } else if (mode === 'append') {
          setOptions((prevOptions) => {
            const combined = [...prevOptions, ...newOptions];
            return combined.filter(
              (option, index, self) =>
                index === self.findIndex((o) => o.code === option.code)
            );
          });
        }

        setPage((prev) => prev + 1);
      } catch (err: any) {
        console.error(
          '[useFetchPotentialAssignees] Error fetching potential assignees:',
          err
        );
        setError(err.message || 'Failed to fetch potential assignees.');
      } finally {
        setIsLoading(false);
      }
    },
    [
      organizationId,
      schemaSystemName,
      perspectiveSystemName,
      limit,
      page,
      isLoading,
      hasMore,
      apiClient,
      searchTerm,
      contextFilters,
    ]
  );

  const triggerInitialFetch = useCallback(() => {
    if (!initialFetchDone && apiClient) {
      setInitialFetchDone(true);
      fetchData('replace');
    }
  }, [initialFetchDone, apiClient, fetchData]);

  const resetData = useCallback(() => {
    setOptions([]);
    setPotentialAssignees([]);
    setPage(1);
    setHasMore(true);
    setError(null);
  }, []);

  return {
    options,
    potentialAssignees,
    isLoading,
    error,
    hasMore,
    fetchData,
    resetData,
    searchTerm,
    setSearchTerm,
    triggerInitialFetch,
  };
};
